import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import { ComplianceResult, ComplianceResultLabels } from '../../../../_shared/enums/compliance-result.enum';
import { PassWidgetComponent } from '../../../../_shared/widgets/asset-compliance-tasks-widgets/pass-widget/pass-widget.component';
import { FailWidgetComponent } from '../../../../_shared/widgets/asset-compliance-tasks-widgets/fail-widget/fail-widget.component';
import { PendingWidgetComponent } from '../../../../_shared/widgets/asset-compliance-tasks-widgets/pending-widget/pending-widget.component';
import { InspectionPassRateWidgetComponent } from '../../../../_shared/widgets/asset-compliance-tasks-widgets/inspection-pass-rate-widget/inspection-pass-rate-widget.component';
import {
  AssetComplianceChecklistResult,
  AssetComplianceChecklistResultRow,
  AssetComplianceChecklistResultsRequest,
  TableColumn
} from '../../../models/compliance-item.model';
import { ComplianceTasksTableComponent } from '../../../../_shared/components/compliance-tasks/compliance-tasks-table.component';
import { ComplianceOverviewService } from '../../../services/compliance-overview.service';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { TranslocoService } from '@jsverse/transloco';
import { CHECKLIST_TYPE_NAME_KEYS, getChecklistTypeOption } from '../../../../_shared/models/checklist-type.model';
import { translateComplianceFrequency } from '../../../../_shared/helpers/compliance-lookup-label.helper';

@Component({
  selector: 'app-asset-periodic-compliance-details',
  standalone: true,
  imports: [
    RouterModule,
    PassWidgetComponent,
    FailWidgetComponent,
    PendingWidgetComponent,
    InspectionPassRateWidgetComponent,
    ComplianceTasksTableComponent
  ],
  templateUrl: './asset-periodic-compliance-details.component.html',
  styleUrl: './asset-periodic-compliance-details.component.scss'
})
export class AssetPeriodicComplianceDetailsComponent implements OnInit, OnDestroy {
  private route: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);
  private complianceOverviewService: ComplianceOverviewService = inject(ComplianceOverviewService);
  private errorService: ErrorHandlerService = inject(ErrorHandlerService);
  private transloco: TranslocoService = inject(TranslocoService);

  private readonly cancelSubject = new Subject<void>();

  assetId: number | null = null;
  complianceId: number | null = null;
  taskId: number | null = null

  assetName: string = '';
  isLoading = true;
  isLoadingMore = false;
  hasError = false;
  currentPage = 1;
  pageSize = 20;
  totalPages = 0;
  totalCount = 0;
  assetComplianceTasks: AssetComplianceChecklistResultRow[] = [];

  columns: TableColumn[] = [
    { header: this.transloco.translate('compliance.overview.periodicDetails.columns.checklistCategory'), value: 'checklistName', width: 'col-2', type: 'text', truncate: true, title: true, bold: true },
    { header: this.transloco.translate('compliance.overview.periodicDetails.columns.frequency'), value: 'frequencyLabel', width: 'col', type: 'text' },
    { header: this.transloco.translate('compliance.overview.periodicDetails.columns.date'), value: 'dateLabel', width: 'col', type: 'text' },
    { header: this.transloco.translate('compliance.overview.periodicDetails.columns.documentOrInspection'), value: 'checklistTypeLabel', width: 'col', type: 'text' },
    { header: this.transloco.translate('compliance.overview.periodicDetails.columns.inspectionUploads'), value: 'attachmentCountLabel', width: 'col', type: 'text' },
    { header: this.transloco.translate('compliance.overview.periodicDetails.columns.notes'), value: 'notes', width: 'col', type: 'nullable-text', mutedValue: this.transloco.translate('compliance.overview.periodicDetails.noNoteProvided') },
    { header: this.transloco.translate('compliance.overview.periodicDetails.columns.taskStatus'), value: 'submissionStatusLabel', width: 'col', type: 'status' },
  ];

  ngOnInit(): void {
    this.assetName = this.route.snapshot.queryParamMap.get('assetName') || '';
    this.assetId = this.getNumberQueryParam('assetId');
    this.complianceId = this.getNumberQueryParam('complianceId');
    this.taskId = this.getNumberQueryParam('id')

    if (!this.assetId || !this.complianceId) {
      this.hasError = true;
      this.isLoading = false;
      return;
    }

    this.resetAndLoad();
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  async getAssetComplianceChecklistResultsAsync(resetResults: boolean = false): Promise<void> {
    if (!this.assetId || !this.complianceId) {
      return;
    }

    this.hasError = false;
    const requestedPage = this.currentPage;
    const request = this.buildRequest(requestedPage);

    try {
      if (requestedPage === 1 || resetResults) {
        this.isLoading = true;
        this.isLoadingMore = false;
        this.cancelSubject.next();
      } else {
        this.isLoadingMore = true;
      }

      const response = await this.complianceOverviewService.getAssetComplianceChecklistResultsAsync(request, this.cancelSubject);
      const pagination = response.data;
      const rows = (pagination?.results ?? []).map(item => this.mapChecklistResult(item));

      if (requestedPage === 1) {
        this.assetComplianceTasks = rows;
      } else {
        this.assetComplianceTasks = [...this.assetComplianceTasks, ...rows];
      }

      this.totalCount = pagination?.totalCount ?? 0;
      this.totalPages = pagination?.totalPages ?? 0;
      this.isLoading = false;
      this.isLoadingMore = false;
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: () => {
          if (requestedPage > 1) {
            this.currentPage--;
          }

          this.hasError = true;
          this.isLoading = false;
          this.isLoadingMore = false;
        }
      });
    }
  }

  onRowClick(item: AssetComplianceChecklistResultRow): void {
    if (!this.assetId || !this.complianceId || !this.taskId) {
      return;
    }

    this.router.navigate(['/asset-management/compliance-overview/asset-routine-compliance-details/inspection-checklist'], {
      queryParams: {
        assetId: this.assetId,
        complianceId: this.complianceId,
        taskId: this.taskId,
        assetName: this.assetName,
        submissionId: item.submissionId ?? 0,
        checklistId: item.checklistId,
        checklist: item.checklistName,
        checklistType: item.checklistType,
        source: 'periodic-details'
      }
    });
  }

  onScrollDown(): void {
    if (this.isLoading || this.isLoadingMore || this.currentPage >= this.totalPages) {
      return;
    }

    this.currentPage++;
    void this.getAssetComplianceChecklistResultsAsync();
  }

  private resetAndLoad(): void {
    this.currentPage = 1;
    this.totalPages = 0;
    this.totalCount = 0;
    this.assetComplianceTasks = [];
    void this.getAssetComplianceChecklistResultsAsync(true);
  }

  private buildRequest(page: number): AssetComplianceChecklistResultsRequest {
    return {
      assetId: this.assetId!,
      complianceId: this.complianceId!,
      taskId: this.taskId!,
      page,
      pageSize: this.pageSize
    };
  }

  private mapChecklistResult(item: AssetComplianceChecklistResult): AssetComplianceChecklistResultRow {
    const date = item.date ? new Date(item.date) : null;

    return {
      ...item,
      date,
      dateLabel: date ? this.formatDate(date) : '',
      frequencyLabel: translateComplianceFrequency(this.transloco, item.frequency),
      checklistTypeLabel: this.getChecklistTypeLabel(item.checklistType),
      attachmentCountLabel: this.getAttachmentCountLabel(item.attachmentCount, item.checklistType),
      submissionStatusLabel: this.getSubmissionStatusLabel(item.submissionStatus)
    };
  }

  private getChecklistTypeLabel(checklistType: string): string {
    const typeId = getChecklistTypeOption(checklistType)?.id;
    const typeLabel = typeId ? this.transloco.translate(CHECKLIST_TYPE_NAME_KEYS[typeId]) : checklistType;
    return this.transloco.translate('compliance.overview.periodicDetails.checklistTypeLabel', { type: typeLabel });
  }

  private getAttachmentCountLabel(attachmentCount: number, checklistType: string): string {
    const isDocument = checklistType.toLowerCase() === 'document';

    if (attachmentCount === 0) {
      return this.transloco.translate(isDocument
        ? 'compliance.overview.periodicDetails.noDocument'
        : 'compliance.overview.periodicDetails.noAttachment');
    }

    const countKey = isDocument
      ? (attachmentCount === 1 ? 'documentCountOne' : 'documentCountMany')
      : (attachmentCount === 1 ? 'attachmentCountOne' : 'attachmentCountMany');
    return this.transloco.translate(`compliance.overview.periodicDetails.${countKey}`, { count: attachmentCount });
  }

  private getSubmissionStatusLabel(status: ComplianceResult): string {
    return ComplianceResultLabels[status] ?? '';
  }

  private getNumberQueryParam(key: string): number | null {
    const value = Number(this.route.snapshot.queryParamMap.get(key));
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  private formatDate(date: Date): string {
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat('en-ZA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  }
}